/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { Box, Divider, Typography } from '@material-ui/core';
import React from 'react';
import { logoBlack } from 'utils/logos';
import { InvitationVerifiedMessage } from 'modules/invitation/InvitationMessages';
import {
  Link as RouterLink,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Link from 'components/ChoreoSystem/Link/Link';
import Button from 'components/ChoreoSystem/Button/Button';
import IdentityProviderInfo from 'modules/login/IdentityProviderInfo/IdentityProviderInfo';
import Tooltip from 'components/ChoreoSystem/Tooltip/Tooltip';
import {
  choreoHomePage,
  privacyPolicy,
  termsOfUse,
  availableLoginRegions,
  enableAsgardeoMaintenanceFlag,
} from 'config';
import { EMBEDDED_SIGN_UP_CONTEST, EMBEDDED_SIGN_UP_PATH } from 'utils/route';
import RegionSelector from 'modules/login/RegionSelector';
import useStyles from './AuthenticationFormCard.styles';

interface AuthenticationFormCardProps {
  children: React.ReactNode;
  title: React.ReactNode;
  testId: string;
  isMobile: boolean;
  orgName?: string;
  isInvitation?: boolean;
  isEmailLogin?: boolean;
  isSignUp?: boolean;
  hideRegionSelector?: boolean;
  /**
   * Id placed on the card title. The auth dialog points its `aria-labelledby`
   * at this so assistive technology announces the modal by name, and focuses
   * the title when it swaps the card. Making the title programmatically
   * focusable is part of that same contract, so `tabIndex={-1}` is derived
   * from this prop rather than exposed as a second one.
   */
  titleId?: string;
  /**
   * Called instead of navigating to `/signup`. Set by the auth dialog, where
   * switching to sign up means swapping the card rather than leaving the page.
   */
  onSignUpClick?: () => void;
  /** Called instead of navigating to `/login`, for the same reason. */
  onSignInClick?: () => void;
}

function AuthenticationFormCard(props: AuthenticationFormCardProps) {
  const {
    children,
    title,
    testId,
    orgName,
    isMobile,
    isInvitation = false,
    isEmailLogin,
    isSignUp = false,
    hideRegionSelector,
    titleId,
    onSignUpClick,
    onSignInClick,
  } = props;
  const classes = useStyles();
  const isEmbeddedSignup = useRouteMatch(EMBEDDED_SIGN_UP_PATH);
  const isEmbeddedContestSignup = useRouteMatch(EMBEDDED_SIGN_UP_CONTEST);
  const isEmbeddedIframe = isEmbeddedSignup || isEmbeddedContestSignup;
  const history = useHistory();
  const location = useLocation();

  return (
    <Box className={classes.authenticationFormCard} data-cyid={testId}>
      {isMobile && (
        <Box mb={3}>
          <img
            alt={logoBlack.alt}
            className={classes.choreoLogo}
            src={logoBlack.src}
            width={logoBlack.width}
          />
        </Box>
      )}
      {isInvitation && orgName && (
        <Box mb={2}>
          <InvitationVerifiedMessage orgName={orgName} />
        </Box>
      )}
      {isEmbeddedIframe ? (
        <>
          <Box mb={5}>
            <Typography
              variant="h2"
              id={titleId}
              tabIndex={titleId ? -1 : undefined}
            >
              {title}
            </Typography>
          </Box>
          {availableLoginRegions.length > 1 && !hideRegionSelector && (
            <RegionSelector
              availableRegions={availableLoginRegions}
              showRegionLabel
            />
          )}
        </>
      ) : (
        <Box
          mb={5}
          display="flex"
          justifyContent={
            availableLoginRegions.length > 1 && !hideRegionSelector
              ? 'space-between'
              : 'center'
          }
          alignItems="center"
          width="100%"
        >
          <Typography
            variant="h2"
            id={titleId}
            tabIndex={titleId ? -1 : undefined}
          >
            {title}
          </Typography>
          {availableLoginRegions.length > 1 && !hideRegionSelector && (
            <RegionSelector availableRegions={availableLoginRegions} />
          )}
        </Box>
      )}

      <Box
        className={
          isEmbeddedIframe
            ? classes.authenticationFormCardContentEmbedded
            : classes.authenticationFormCardContent
        }
      >
        {children}
      </Box>
      <Box mt={3} width="100%">
        {(isEmailLogin || isSignUp) && (
          <Box mb={3} width="100%">
            {isEmailLogin && (
              <Box className={classes.signupCtaSection}>
                <Divider className={classes.signupDivider} />
                <Tooltip
                  disabled={!enableAsgardeoMaintenanceFlag}
                  title={
                    <FormattedMessage
                      id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.maintenance.tooltip"
                      defaultMessage={
                        'New registrations are temporarily unavailable during this period ' +
                        'due to an ongoing maintenance. Please come back and try again shortly.'
                      }
                    />
                  }
                  arrow
                >
                  <span className={classes.signupButtonWrapper}>
                    <Button
                      component={RouterLink}
                      className={classes.signupButton}
                      rel="noopener"
                      to={{ pathname: '/signup' }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (onSignUpClick) {
                          onSignUpClick();
                        } else {
                          history.push('/signup', location.state);
                        }
                      }}
                      testId={`${testId}-signup`}
                      color="secondary"
                      fullWidth
                    >
                      <FormattedMessage
                        id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.createAccount"
                        defaultMessage="New here? Sign up"
                      />
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
            {isSignUp && (
              <Typography variant="body1" className={classes.choreoSignUpLink}>
                <FormattedMessage
                  id="modules.signup.signupCard.signupCard.accountExists"
                  defaultMessage="Already have an account?&nbsp;"
                />
                {isEmbeddedIframe ? (
                  <Link
                    underline="none"
                    component={RouterLink}
                    rel="noopener"
                    to="/"
                    testId={`${testId}-signin`}
                    target="_blank"
                  >
                    <FormattedMessage
                      id="modules.signup.signupCard.signupCard.signin"
                      defaultMessage="Sign In"
                    />
                  </Link>
                ) : (
                  <Link
                    underline="none"
                    component={RouterLink}
                    rel="noopener"
                    to="/login"
                    onClick={(event: React.MouseEvent) => {
                      if (onSignInClick) {
                        event.preventDefault();
                        event.stopPropagation();
                        onSignInClick();
                      }
                    }}
                    testId={`${testId}-signin`}
                  >
                    <FormattedMessage
                      id="modules.signup.signupCard.signupCard.signin"
                      defaultMessage="Sign In"
                    />
                  </Link>
                )}
              </Typography>
            )}
          </Box>
        )}
        {!isEmbeddedIframe && (
          <>
            <Box>
              <Typography variant="body1" className={classes.choreMoreDetails}>
                <FormattedMessage
                  id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.moreDetails.text"
                  defaultMessage="More details at&nbsp;"
                />
                <Link
                  underline="none"
                  component={RouterLink}
                  rel="noopener"
                  target="_blank"
                  to={{ pathname: choreoHomePage }}
                  variant="body1"
                  testId={`${testId}-choreo-homepage`}
                >
                  <FormattedMessage
                    id="components.ChoreoSystem.Authenticate.AuthenticationFormCard.moreDetails.linkText"
                    defaultMessage="wso2.com/integration-platform"
                  />
                </Link>
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              mt={0.5}
            >
              <Box mr={1}>
                <Link
                  underline="none"
                  component={RouterLink}
                  target="_blank"
                  rel="noopener"
                  to={{ pathname: privacyPolicy }}
                  variant="body1"
                  className={classes.otherLinks}
                  testId={`${testId}-privacy-policy`}
                >
                  <FormattedMessage
                    id="layouts.main.components.Footer.privacy.policy"
                    defaultMessage="Privacy Policy"
                  />
                </Link>
              </Box>
              <Divider orientation="vertical" light flexItem />
              <Box ml={1}>
                <Link
                  underline="none"
                  component={RouterLink}
                  target="_blank"
                  rel="noopener"
                  to={{ pathname: termsOfUse }}
                  className={classes.otherLinks}
                  variant="body1"
                  testId={`${testId}-terms-of-use`}
                >
                  <FormattedMessage
                    id="layouts.main.components.Footer.terms.of.use"
                    defaultMessage="Terms of Use"
                  />
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box pt={4}>
        <IdentityProviderInfo />
      </Box>
    </Box>
  );
}

export default AuthenticationFormCard;
